<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading && contract">

    <app-page-detail-hero
        eyebrow="CRM Contracts"
        icon="fa-solid fa-file-contract"
        [title]="'Contract# ' + contract.contractNumber"
        [subtitle]="'Seller: ' + contract.firstParty.name + ' / Buyer: ' + contract.secondParty.name">
        <div tags>
            <p-tag icon="pi pi-file-contract" [value]="contract.contractStatus"></p-tag>
        </div>
        <div actions>
            <p-button label="Edit Contract" icon="fa-solid fa-pencil" severity="secondary" (onClick)="editContract()"></p-button>
            <p-button label="Add Revision" icon="fa-solid fa-plus" (onClick)="addRevision()"></p-button>
            <p-button label="Create Logistics Project" icon="pi pi-briefcase" severity="secondary" [outlined]="true" (onClick)="createLogisticsProjectFromContract()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="getContractMetrics()"></app-page-metric-strip>

    <div class="grid">
        <div class="col-12 lg:col-6">
            <app-page-data-panel title="Seller (First Party)" subtitle="Selling party contact details used for delivery rate cards.">
                <div class="flex gap-3 align-items-start">
                    <p-image src="{{ contract.firstParty.logo != null ? contract.firstParty.logo : 'https://cdn-icons-png.flaticon.com/512/4299/4299051.png' }}" height="50"></p-image>
                    <div class="flex flex-column gap-2">
                        <div class="flex align-items-center gap-2">
                            <p-avatar image="{{ contract.firstPartyRep.imageUrl != null ? contract.firstPartyRep.imageUrl : 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png' }}" shape="circle"></p-avatar>
                            <b>{{ contract.firstPartyRep.name }}</b>
                        </div>
                        <span><i class="fa-solid fa-phone mr-2"></i>{{ contract.firstPartyRep.phoneNumber }}</span>
                        <span><i class="fa-solid fa-envelope mr-2"></i>{{ contract.firstPartyRep.emailAddress }}</span>
                        <span><i class="fa-solid fa-building mr-2"></i>{{ contract.firstParty.name }}</span>
                        <a href="https://maps.google.com?q={{ contract.firstParty.location.latitude }},{{ contract.firstParty.location.longitude }}" target="_blank">
                            <i class="fa-solid fa-location-dot mr-2"></i>{{pickupSubLocality}}, {{pickupLocality}}
                        </a>
                    </div>
                </div>
            </app-page-data-panel>
        </div>
        <div class="col-12 lg:col-6">
            <app-page-data-panel title="Buyer (Second Party)" subtitle="Buying party contact details used for delivery rate cards.">
                <div class="flex gap-3 align-items-start">
                    <p-image src="{{ contract.secondParty.logo != null ? contract.secondParty.logo : 'https://cdn-icons-png.flaticon.com/512/4299/4299051.png' }}" height="50"></p-image>
                    <div class="flex flex-column gap-2">
                        <div class="flex align-items-center gap-2">
                            <p-avatar image="{{ contract.secondPartyRep.imageUrl != null ? contract.secondPartyRep.imageUrl : 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png' }}" shape="circle"></p-avatar>
                            <b>{{ contract.secondPartyRep.name }}</b>
                        </div>
                        <span><i class="fa-solid fa-phone mr-2"></i>{{ contract.secondPartyRep.phoneNumber }}</span>
                        <span><i class="fa-solid fa-envelope mr-2"></i>{{ contract.secondPartyRep.emailAddress }}</span>
                        <span><i class="fa-solid fa-building mr-2"></i>{{ contract.secondParty.name }}</span>
                        <a href="https://maps.google.com?q={{ contract.secondParty.location.latitude }},{{ contract.secondParty.location.longitude }}" target="_blank">
                            <i class="fa-solid fa-location-dot mr-2"></i>{{dropoffSubLocality}}, {{dropoffLocality}}
                        </a>
                    </div>
                </div>
            </app-page-data-panel>
        </div>
    </div>

    <app-page-data-panel *ngIf="crmContext" title="CRM Context" subtitle="Linked customer, sales activity, documents, reminders, and approved quotation context.">
        <div class="grid">
            <div class="col-12 md:col-4">
                <div class="flex flex-column gap-2">
                    <strong><i class="fa-solid fa-building-user mr-2"></i>{{ crmContext.client?.companyName || 'No CRM client linked' }}</strong>
                    <span class="text-600">KAM: {{ crmContext.kamCustomerId || contract.kamCustomerId || '-' }}</span>
                    <span class="text-600">Accounts: {{ crmContext.accounts?.length || 0 }}</span>
                    <span class="text-600">Next Expiry: {{ crmContext.nextExpiryDate ? (crmContext.nextExpiryDate | date:'mediumDate') : '-' }}</span>
                </div>
            </div>
            <div class="col-12 md:col-4">
                <div class="flex flex-column gap-2">
                    <strong><i class="fa-solid fa-folder-open mr-2"></i>Evidence</strong>
                    <span class="text-600">Documents: {{ (crmContext.documents?.length || 0) + (crmContext.registrationAttachments?.length || 0) }}</span>
                    <span class="text-600">Reminders: {{ crmContext.reminders?.length || 0 }}</span>
                    <span class="text-600">Activities: {{ crmContext.activities?.length || 0 }}</span>
                </div>
            </div>
            <div class="col-12 md:col-4">
                <div class="flex flex-column gap-2">
                    <strong><i class="fa-solid fa-file-invoice-dollar mr-2"></i>Commercial Health</strong>
                    <span class="text-600">Approved quotes awaiting contract: {{ crmContext.approvedQuotationAwaitingContractCount || 0 }}</span>
                    <div class="flex flex-wrap gap-2">
                        <p-tag *ngFor="let flag of getHealthFlags()" [value]="flag" severity="info"></p-tag>
                        <span *ngIf="getHealthFlags().length === 0" class="text-600">No flags</span>
                    </div>
                </div>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="sourceQuotations.length > 0" title="Source Quotations" subtitle="Quotation lineage and promoted commercial context for this agreement." [resultText]="sourceQuotations.length + ' linked'">
        <div class="grid">
            <div class="col-12 md:col-6" *ngFor="let link of sourceQuotations">
                <div class="source-quotation-card">
                    <div class="flex align-items-center justify-content-between gap-3">
                        <strong><i class="fa-solid fa-file-invoice-dollar mr-2"></i>{{ link.quotationNumber || ('Quotation #' + link.quotationId) }}</strong>
                        <p-tag [value]="link.quotationStatus || link.status" severity="info"></p-tag>
                    </div>
                    <div class="source-quotation-meta">
                        <span>Total: {{ link.quotationTotalAmount || 0 | number:'1.2-2' }}</span>
                        <span>Revision: {{ link.revisionNumber || link.revisionId || '-' }}</span>
                        <span>Review notes: {{ link.reviewNotesCount || 0 }}</span>
                    </div>
                    <p class="text-600 mt-2 mb-0">{{ link.deviationSummary || 'No deviation summary recorded.' }}</p>
                    <div class="flex flex-wrap gap-2 mt-3">
                        <p-tag *ngFor="let item of link.promotions || []" [value]="item.targetType + (item.status ? ' / ' + item.status : '')" severity="secondary"></p-tag>
                    </div>
                </div>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-data-panel title="Contract Revisions" subtitle="Review agreement versions, rate cards, and stored agreement files.">
        <div actions>
            <p-button label="Add Revision" severity="help" icon="fa-solid fa-plus" (onClick)="addRevision()"></p-button>
        </div>

        <p-tabs *ngIf="contract.revisions.length > 0" [value]="currentRevisionNumber" (valueChange)="onRevisionTabChange($event)">
            <p-tablist>
                <p-tab *ngFor="let revision of contract.revisions; let i = index" [value]="i">
                    <div class="flex align-items-center gap-2">
                        <i class="fa-solid fa-code-branch"></i>
                        <span>Revision# {{ revision.revisionNumber }}</span>
                        <p-tag *ngIf="revision.status === 'ACTIVE'" icon="pi pi-check" severity="success" value="ACTIVE"></p-tag>
                        <p-tag *ngIf="revision.status === 'DRAFT'" icon="pi pi-user" severity="info" value="DRAFT"></p-tag>
                    </div>
                </p-tab>
            </p-tablist>
            <p-tabpanels>
                <p-tabpanel *ngFor="let revision of contract.revisions; let i = index" [value]="i">
                    <div class="flex justify-content-end gap-2 mb-3">
                        <p-button label="Rate Cards" severity="secondary" icon="fa-solid fa-route" (onClick)="openRateCardsWorkspace(revision)"></p-button>
                        <p-button label="Document Workspace" severity="secondary" icon="fa-solid fa-file-contract" (onClick)="openDocumentWorkspace()"></p-button>
                        <p-button label="Edit Revision" severity="info" icon="fa-solid fa-pencil" (onClick)="editRevision()"></p-button>
                    </div>

                    <app-page-data-panel title="Lifecycle Actions" subtitle="Controlled transitions replace direct status edits. Activation blockers are shown before status changes." [resultText]="lifecycle?.status || revision.status">
                        <div class="lifecycle-control-bar">
                            <span class="lifecycle-control-label">Available actions</span>
                            <div class="lifecycle-action-buttons">
                                <button
                                    *ngFor="let action of lifecycleActions()"
                                    type="button"
                                    class="lifecycle-action-button"
                                    [class.is-primary]="action.action === 'SUBMIT'"
                                    [class.is-success]="action.action === 'APPROVE' || action.action === 'ACTIVATE'"
                                    [class.is-danger]="action.action === 'REJECT' || action.action === 'TERMINATE'"
                                    [disabled]="!!lifecycleActionInProgress"
                                    (click)="runLifecycleAction(action.action)">
                                    <i [class]="action.icon" aria-hidden="true"></i>
                                    <span>{{ lifecycleActionInProgress === action.action ? 'Working...' : action.label }}</span>
                                </button>
                            </div>
                        </div>
                        <div class="grid">
                            <div class="col-12 lg:col-5">
                                <textarea pTextarea rows="3" class="w-full" [(ngModel)]="transitionReason" placeholder="Reason or approval comments"></textarea>
                            </div>
                            <div class="col-12 lg:col-7">
                                <div class="flex flex-wrap gap-3">
                                    <label class="flex align-items-center gap-2"><p-checkbox [(ngModel)]="activationOptions.signatureOverride" [binary]="true"></p-checkbox><span>Signature override</span></label>
                                    <label class="flex align-items-center gap-2"><p-checkbox [(ngModel)]="activationOptions.approvalOverride" [binary]="true"></p-checkbox><span>Approval override</span></label>
                                    <label class="flex align-items-center gap-2"><p-checkbox [(ngModel)]="activationOptions.documentWaiver" [binary]="true"></p-checkbox><span>Document waiver</span></label>
                                    <label class="flex align-items-center gap-2"><p-checkbox [(ngModel)]="activationOptions.rateWaiver" [binary]="true"></p-checkbox><span>Rate waiver</span></label>
                                </div>
                            </div>
                            <div class="col-12" *ngIf="lifecycleBlockers.length > 0">
                                <div class="flex flex-wrap gap-2">
                                    <p-tag *ngFor="let blocker of lifecycleBlockers" [severity]="blockerSeverity(blocker)" [value]="blocker.code + ': ' + blocker.message"></p-tag>
                                </div>
                            </div>
                            <div class="col-12" *ngIf="lifecycleNotice">
                                <div class="lifecycle-notice" [class.is-blocked]="lifecycleBlockers.length > 0">
                                    {{ lifecycleNotice }}
                                </div>
                            </div>
                        </div>
                    </app-page-data-panel>

                    <p-tabs [value]="activeTabIndex" (valueChange)="onTabChange($event)">
                        <p-tablist>
                            <p-tab [value]="0">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-file-lines"></i>
                                    <span>Revision Details</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="1">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-route"></i>
                                    <span>Delivery Rate Cards</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="2">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-folder-open"></i>
                                    <span>Contract Files</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="3">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-file-contract"></i>
                                    <span>Generated Documents</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="4">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-user-check"></i>
                                    <span>Approvals</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="5">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-rotate"></i>
                                    <span>Renewals</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="6">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-clipboard-check"></i>
                                    <span>Obligations</span>
                                </div>
                            </p-tab>
                            <p-tab [value]="7">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-solid fa-clock-rotate-left"></i>
                                    <span>Audit</span>
                                </div>
                            </p-tab>
                        </p-tablist>
                        <p-tabpanels>
                            <p-tabpanel [value]="0">
                                <div class="grid">
                                    <div class="lg:col-6 sm:col-12" *ngFor="let field of revisionFields">
                                        <span class="font-bold">{{field.label}}:</span>&nbsp;
                                        <span *ngIf="field.type === 'date'">{{ showAsDate(revision[field.key]) }}</span>
                                        <span *ngIf="field.type === 'boolean'">{{ revision[field.key] ? 'Yes' : 'No' }}</span>
                                        <span *ngIf="field.type === 'string'">{{ revision[field.key] }}</span>
                                    </div>
                                </div>
                            </p-tabpanel>
                            <p-tabpanel [value]="1">
                                <div class="flex align-items-center justify-content-between gap-3 p-3 border-1 surface-border border-round">
                                    <div>
                                        <strong>{{ getRateCardCount(revision) }} delivery rate cards</strong>
                                        <p class="text-600 mt-1 mb-0">Manage lanes, vehicles, and template imports from the shipment rate-card workspace.</p>
                                    </div>
                                    <p-button label="Open Rate Cards" icon="fa-solid fa-route" (onClick)="openRateCardsWorkspace(revision)"></p-button>
                                </div>
                            </p-tabpanel>
                            <p-tabpanel [value]="2">
                                <p-table [value]="contractFilesList">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>File Name</th>
                                            <th>File Type</th>
                                            <th>Created At</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-contractFile>
                                        <tr>
                                            <td><a href="{{contractFile.fileUrl}}" target="_blank">{{contractFile.fileName}}</a></td>
                                            <td>{{contractFile.fileType}}</td>
                                            <td>{{showDate(contractFile.createdAt)}}</td>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="summary">
                                        <div class="flex align-items-center justify-content-between">
                                            In total there are {{contractFilesCount}} files.
                                        </div>
                                    </ng-template>
                                </p-table>
                            </p-tabpanel>
                            <p-tabpanel [value]="3">
                                <div class="flex align-items-center justify-content-between gap-2">
                                    <div>
                                        <h3 class="m-0">Document Generation</h3>
                                        <p class="text-600 mt-1">Open the document workspace to edit rich text sections, generate drafts, create PDF links, and record signed agreement metadata.</p>
                                    </div>
                                    <p-button label="Open Workspace" icon="fa-solid fa-file-contract" (onClick)="openDocumentWorkspace()"></p-button>
                                </div>
                            </p-tabpanel>
                            <p-tabpanel [value]="4">
                                <p-table [value]="lifecycle?.approvals || []" styleClass="p-datatable-sm p-datatable-gridlines">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>#</th>
                                            <th>Role</th>
                                            <th>Approver</th>
                                            <th>Status</th>
                                            <th>Decision</th>
                                            <th>Decided At</th>
                                            <th>Comments</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-step>
                                        <tr>
                                            <td>{{ step.sequenceNo }}</td>
                                            <td>{{ step.approverRole || '-' }}</td>
                                            <td>{{ step.approverUserId || '-' }}</td>
                                            <td><p-tag [value]="step.status || 'PENDING'" severity="info"></p-tag></td>
                                            <td>{{ step.decision || '-' }}</td>
                                            <td>{{ step.decidedAt | date:'medium' }}</td>
                                            <td>{{ step.comments || '-' }}</td>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage">
                                        <tr><td colspan="7">No approval decisions yet.</td></tr>
                                    </ng-template>
                                </p-table>
                            </p-tabpanel>
                            <p-tabpanel [value]="5">
                                <div class="grid" *ngIf="lifecycle?.renewal as renewal">
                                    <div class="col-12 md:col-3"><b>Owner</b><br>{{ renewal.renewalOwnerId || '-' }}</div>
                                    <div class="col-12 md:col-3"><b>Opt-out Deadline</b><br>{{ renewal.optOutDeadline | date:'mediumDate' }}</div>
                                    <div class="col-12 md:col-3"><b>Renewal Due</b><br>{{ renewal.renewalDueDate | date:'mediumDate' }}</div>
                                    <div class="col-12 md:col-3"><b>Status</b><br><p-tag [value]="renewal.decisionStatus || 'PENDING'" severity="warn"></p-tag></div>
                                    <div class="col-12">
                                        <textarea pTextarea rows="3" class="w-full" [(ngModel)]="renewal.decisionReason" placeholder="Renewal decision reason"></textarea>
                                    </div>
                                    <div class="col-12">
                                        <p-button label="Save Renewal" icon="fa-solid fa-floppy-disk" (onClick)="saveRenewal()"></p-button>
                                    </div>
                                </div>
                            </p-tabpanel>
                            <p-tabpanel [value]="6">
                                <p-table [value]="lifecycle?.obligations || []" styleClass="p-datatable-sm p-datatable-gridlines">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>Type</th>
                                            <th>Title</th>
                                            <th>Owner</th>
                                            <th>Due</th>
                                            <th>Status</th>
                                            <th>Evidence</th>
                                            <th style="width: 8rem">Action</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-obligation>
                                        <tr>
                                            <td>{{ obligation.obligationType }}</td>
                                            <td>{{ obligation.title }}</td>
                                            <td>{{ obligation.ownerId || '-' }}</td>
                                            <td>{{ obligation.dueDate | date:'mediumDate' }}</td>
                                            <td><p-tag [value]="obligation.status || 'OPEN'" severity="info"></p-tag></td>
                                            <td>{{ obligation.completionEvidence || '-' }}</td>
                                            <td><p-button label="Done" size="small" severity="success" [disabled]="obligation.status === 'COMPLETED'" (onClick)="markObligationDone(obligation)"></p-button></td>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage">
                                        <tr><td colspan="7">No obligations generated yet.</td></tr>
                                    </ng-template>
                                </p-table>
                            </p-tabpanel>
                            <p-tabpanel [value]="7">
                                <p-table [value]="lifecycle?.events || []" styleClass="p-datatable-sm p-datatable-gridlines">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>Event</th>
                                            <th>Actor</th>
                                            <th>Time</th>
                                            <th>Status</th>
                                            <th>Reason</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-event>
                                        <tr>
                                            <td>{{ event.eventType }}</td>
                                            <td>{{ event.actorId || '-' }}</td>
                                            <td>{{ event.eventAt | date:'medium' }}</td>
                                            <td>{{ event.previousStatus || '-' }} -> {{ event.newStatus || '-' }}</td>
                                            <td>{{ event.reason || '-' }}</td>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage">
                                        <tr><td colspan="5">No lifecycle events yet.</td></tr>
                                    </ng-template>
                                </p-table>
                            </p-tabpanel>
                        </p-tabpanels>
                    </p-tabs>
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
        <p *ngIf="contract.revisions.length == 0">No revisions found.</p>
    </app-page-data-panel>
</app-page-shell>

<p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
